import {
  isLayerSource,
  layerPatterns,
  LayerType,
  type Layer,
} from '@pn/core/domain/layer';
import { handleError } from '@pn/core/errors/handleError';
import { assert } from '@pn/core/utils/assert';
import { isNil, pick } from 'lodash-es';
import { mapboxLayerStyleMapper } from './mapboxLayerStyleMapper';

export const POINTS_LAYER_MIN_ZOOM = 6;

export const mapboxLayerMapper = {
  toDomainLayer: (
    mapboxLayer: mapboxgl.Layer,
    renderAsPoints = false
  ): Layer => {
    assert(isLayerSource(mapboxLayer.source), 'Not a valid layer source');

    return {
      id: mapboxLayer.id,
      type: toDomainLayerType(mapboxLayer),
      style: mapboxLayerStyleMapper.toDomainLayerStyle(
        pick(mapboxLayer, ['layout', 'paint'])
      ),
      renderAsPoints,
      source: mapboxLayer.source,
      sourceLayer: mapboxLayer['source-layer'],
      metadata: validateMetadata(mapboxLayer.metadata),
    };
  },
  toTargetLayer: (layer: Layer): mapboxgl.Layer => {
    return {
      id: layer.id,
      type: layerTypeMapping[layer.type],
      source: layer.source,
      metadata: layer.metadata,
      minzoom: layer.renderAsPoints ? POINTS_LAYER_MIN_ZOOM : 0,
      ...mapboxLayerStyleMapper.toTargetLayerStyle(layer.style, layer.type),
      ...(layer.sourceLayer ? { 'source-layer': layer.sourceLayer } : {}),
    };
  },
};

function validateMetadata(metadata: Layer['metadata']): Layer['metadata'] {
  if (isNil(metadata)) return undefined;
  if (metadata.type !== 'drawing' && !layerPatterns.includes(metadata.type)) {
    handleError({
      error: new Error(`Unhandled layer metadata: ${metadata.type}`),
    });
    return undefined;
  } else {
    return metadata;
  }
}

const layerTypeMapping: Record<LayerType, mapboxgl.Layer['type']> = {
  [LayerType.Line]: 'line',
  [LayerType.Polygon]: 'fill',
  [LayerType.Circle]: 'circle',
  [LayerType.Text]: 'symbol',
  [LayerType.Icon]: 'symbol',
  [LayerType.Raster]: 'raster',
  [LayerType.Background]: 'background',
};

const inverseLayerTypeMapping = Object.entries(layerTypeMapping).reduce<
  Record<mapboxgl.Layer['type'], LayerType>
>((acc, [key, value]) => {
  acc[value] = key as LayerType;
  return acc;
}, {});

function toDomainLayerType(mapboxLayer: mapboxgl.Layer): LayerType {
  if (mapboxLayer.type === 'symbol' && !isNil(mapboxLayer.layout)) {
    const hasSymbolField =
      'text-field' in mapboxLayer.layout &&
      mapboxLayer.layout['text-field'] === '{symbol}';
    const hasIconImage =
      'icon-image' in mapboxLayer.layout &&
      !isNil(mapboxLayer.layout['icon-image']);

    return hasSymbolField || hasIconImage ? LayerType.Icon : LayerType.Text;
  }

  return inverseLayerTypeMapping[mapboxLayer.type];
}
